import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Check, ChevronsUpDown, Globe, Download, Trash2, Loader2, Sun, Moon, Monitor } from "lucide-react";
import { symbolFor } from "@/lib/currency";
import { AccountAPI } from "@/lib/api";
import { toast } from "sonner";
import DeleteAccountDialog from "./DeleteAccountDialog";
import { useTheme } from "@/lib/theme";
import ChargingRatesSection from "@/components/ChargingRatesSection";
import {
  getBrowserTz,
  getSupportedTimezones,
  isValidTz,
  partsInTz,
} from "@/lib/tz";

const CURRENCIES = [
  ["USD", "US Dollar"],
  ["CAD", "Canadian Dollar"],
  ["EUR", "Euro"],
  ["GBP", "British Pound"],
  ["AUD", "Australian Dollar"],
  ["NZD", "New Zealand Dollar"],
  ["INR", "Indian Rupee"],
  ["JPY", "Japanese Yen"],
  ["CNY", "Chinese Yuan"],
  ["CHF", "Swiss Franc"],
  ["SEK", "Swedish Krona"],
  ["NOK", "Norwegian Krone"],
  ["DKK", "Danish Krone"],
  ["BRL", "Brazilian Real"],
  ["MXN", "Mexican Peso"],
  ["ZAR", "South African Rand"],
  ["SGD", "Singapore Dollar"],
  ["AED", "UAE Dirham"],
  ["TRY", "Turkish Lira"],
  ["PLN", "Polish Zloty"],
];

/**
 * Segmented control for theme mode. Pure presentational — reads from the
 * shared ThemeProvider so changes propagate to the whole app instantly.
 */
const THEME_OPTIONS = [
  { value: "system", label: "System", Icon: Monitor },
  { value: "light", label: "Light", Icon: Sun },
  { value: "dark", label: "Dark", Icon: Moon },
];

function ThemeSegmented() {
  const { mode, setMode } = useTheme();
  return (
    <div
      className="inline-flex w-full items-center bg-[var(--bg-input)] border border-[var(--border-subtle)] p-0.5"
      style={{ borderRadius: 2 }}
      data-testid="settings-theme-toggle"
    >
      {THEME_OPTIONS.map(({ value, label, Icon }) => {
        const active = mode === value;
        return (
          <button
            key={value}
            type="button"
            onClick={() => setMode(value)}
            data-testid={`settings-theme-${value}`}
            aria-pressed={active}
            className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs tracking-wider transition-colors ${
              active
                ? "bg-[var(--bg-inverse)] text-[var(--text-inverse)]"
                : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"
            }`}
            style={{ borderRadius: 2 }}
          >
            <Icon className="w-3 h-3" strokeWidth={1.75} />
            {label}
          </button>
        );
      })}
    </div>
  );
}

export default function SettingsDialog({ open, onOpenChange, settings, onSave, unit, onUnitChange }) {
  const [currency, setCurrency] = useState(settings?.currency || "USD");
  const [deductibleRate, setDeductibleRate] = useState(
    settings?.deductible_rate_per_km != null ? String(settings.deductible_rate_per_km) : "0.70"
  );
  const browserTz = useMemo(() => getBrowserTz(), []);
  // Settings.timezone falls back to the browser-detected zone on first load.
  // User-selected value always overrides browser detection thereafter.
  const [tz, setTz] = useState(
    isValidTz(settings?.timezone) ? settings.timezone : browserTz
  );
  const [tzOpen, setTzOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Opens the export-options confirmation dialog. Actual export is
  // triggered from within that dialog after the user (optionally) opts
  // to include archived vehicles' rows.
  const [exportOptionsOpen, setExportOptionsOpen] = useState(false);
  const [exportIncludeArchived, setExportIncludeArchived] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const { filename } = await AccountAPI.exportData({
        includeArchived: exportIncludeArchived,
      });
      toast.success(`Export ready: ${filename}`);
      setExportOptionsOpen(false);
    } catch (e) {
      toast.error(e?.message || "Could not generate the export.");
    } finally {
      setExporting(false);
    }
  };

  // Reset the archived-toggle every time the confirm dialog opens so users
  // consciously opt in per-export rather than silently inheriting a prior
  // "include" choice.
  useEffect(() => {
    if (exportOptionsOpen) setExportIncludeArchived(false);
  }, [exportOptionsOpen]);

  // Pre-computed once per dialog open — IANA list can have ~400 entries.
  const supportedTimezones = useMemo(() => getSupportedTimezones(), []);

  // Current UTC offset preview ("UTC-04:00" etc) for the selected zone.
  const tzOffsetLabel = useMemo(() => {
    try {
      const fmt = new Intl.DateTimeFormat("en-US", {
        timeZone: tz,
        timeZoneName: "shortOffset",
      });
      const part = fmt.formatToParts(new Date()).find((p) => p.type === "timeZoneName");
      return part?.value || "";
    } catch (_e) {
      return "";
    }
  }, [tz]);

  // Live local-time preview for the selected zone — confirms the user picked the right one.
  const tzPreview = useMemo(() => {
    try {
      const p = partsInTz(new Date(), tz);
      const hh = String(p.hour).padStart(2, "0");
      const mm = String(p.minute).padStart(2, "0");
      return `${p.year}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")} · ${hh}:${mm}`;
    } catch (_e) {
      return "";
    }
  }, [tz]);

  useEffect(() => {
    if (open && settings) {
      setCurrency(settings.currency || "USD");
      setDeductibleRate(String(settings.deductible_rate_per_km ?? 0.7));
      setTz(isValidTz(settings.timezone) ? settings.timezone : browserTz);
    }
  }, [open, settings, browserTz]);

  const submit = async () => {
    setSaving(true);
    try {
      await onSave({
        currency,
        currency_symbol: symbolFor(currency),
        deductible_rate_per_km: parseFloat(deductibleRate) || 0,
        timezone: tz,
      });
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[var(--bg-canvas)] border-[var(--border-subtle)] text-[var(--text-primary)] sm:max-w-[440px] max-h-[85vh] overflow-y-auto"
        data-testid="settings-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-light tracking-tight">
            Settings
          </DialogTitle>
          <DialogDescription className="text-[var(--text-muted)]">
            App-wide preferences. Units, currency and deductible rate.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Appearance / theme */}
          <div className="space-y-2" data-testid="settings-theme-section">
            <Label className="label-tiny">Appearance</Label>
            <ThemeSegmented />
          </div>

          {onUnitChange && (
            <div className="space-y-2">
              <Label className="label-tiny">Distance Unit</Label>
              <div
                className="inline-flex items-center bg-[var(--bg-input)] border border-[var(--border-subtle)] p-0.5"
                style={{ borderRadius: 2 }}
                data-testid="km-miles-toggle"
              >
                <button
                  type="button"
                  onClick={() => onUnitChange("km")}
                  data-testid="unit-km-btn"
                  className={`tap px-4 py-1.5 text-xs font-mono-data tracking-[0.15em] transition-colors ${
                    unit === "km" ? "bg-[var(--bg-inverse)] text-[var(--text-inverse)]" : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                  }`}
                  style={{ borderRadius: 1 }}
                >
                  KM
                </button>
                <button
                  type="button"
                  onClick={() => onUnitChange("mi")}
                  data-testid="unit-mi-btn"
                  className={`tap px-4 py-1.5 text-xs font-mono-data tracking-[0.15em] transition-colors ${
                    unit === "mi" ? "bg-[var(--bg-inverse)] text-[var(--text-inverse)]" : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                  }`}
                  style={{ borderRadius: 1 }}
                >
                  MI
                </button>
              </div>
              <p className="text-[11px] text-[var(--text-muted)]">
                Applies across all KPIs, charts and inputs. Saved instantly to this device.
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label className="label-tiny">Currency</Label>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger
                className="bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-primary)]"
                data-testid="settings-currency-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-primary)] max-h-[280px]">
                {CURRENCIES.map(([code, name]) => (
                  <SelectItem key={code} value={code}>
                    <span className="font-mono-data text-xs mr-2 text-[var(--text-muted)]">{symbolFor(code)}</span>
                    {code} <span className="text-[var(--text-muted)] ml-1">· {name}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-[var(--text-muted)]">
              Used across energy spend, cost / km and deduction estimates.
            </p>
          </div>

          <div className="space-y-2">
            <Label className="label-tiny flex items-center gap-1.5">
              <Globe className="w-3 h-3 text-[var(--text-muted)]" strokeWidth={2} />
              <span>Timezone</span>
              {tzOffsetLabel && (
                <span className="text-[10px] font-mono-data text-[var(--text-muted)] uppercase tracking-wider">
                  · {tzOffsetLabel}
                </span>
              )}
            </Label>
            <Popover open={tzOpen} onOpenChange={setTzOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  role="combobox"
                  aria-expanded={tzOpen}
                  className="tap flex w-full items-center justify-between bg-[var(--bg-input)] border border-[var(--border-subtle)] text-[var(--text-primary)] px-3 h-10 text-sm hover:border-[#2a2a2a] transition-colors"
                  style={{ borderRadius: 2 }}
                  data-testid="settings-timezone-trigger"
                >
                  <span className="truncate font-mono-data text-[13px]">
                    {tz || "Select timezone..."}
                  </span>
                  <ChevronsUpDown className="w-3.5 h-3.5 text-[var(--text-muted)] shrink-0 ml-2" strokeWidth={1.75} />
                </button>
              </PopoverTrigger>
              <PopoverContent
                className="w-[--radix-popover-trigger-width] p-0 bg-[var(--bg-canvas)] border-[var(--border-subtle)]"
                align="start"
              >
                <Command className="bg-transparent" data-testid="settings-timezone-command">
                  <CommandInput
                    placeholder="Search zones..."
                    className="h-9 text-sm"
                    data-testid="settings-timezone-search"
                  />
                  <CommandList className="max-h-[260px]">
                    <CommandEmpty className="py-4 text-center text-xs text-[var(--text-muted)]">
                      No timezone found.
                    </CommandEmpty>
                    <CommandGroup>
                      {supportedTimezones.map((z) => (
                        <CommandItem
                          key={z}
                          value={z}
                          onSelect={(v) => {
                            setTz(v);
                            setTzOpen(false);
                          }}
                          className="text-[13px] font-mono-data data-[selected=true]:bg-[var(--bg-emphasis)] cursor-pointer"
                          data-testid={`settings-timezone-option-${z}`}
                        >
                          <Check
                            className={`w-3.5 h-3.5 mr-2 ${tz === z ? "opacity-100 text-[#34D17A]" : "opacity-0"}`}
                            strokeWidth={2.5}
                          />
                          {z}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <p className="text-[11px] text-[var(--text-muted)]">
              {tzPreview ? (
                <>
                  Local time: <span className="font-mono-data text-[var(--text-primary)]">{tzPreview}</span>
                </>
              ) : (
                <>Used for trip dates, monthly pacing, and analytics grouping.</>
              )}
              {tz && tz !== browserTz && (
                <span className="block mt-1 text-[var(--text-muted)]">
                  Browser detected:{" "}
                  <button
                    type="button"
                    onClick={() => setTz(browserTz)}
                    className="font-mono-data text-[var(--text-primary)] hover:text-[#E82127] underline-offset-2 hover:underline"
                    data-testid="settings-timezone-revert"
                  >
                    {browserTz}
                  </button>
                </span>
              )}
            </p>
          </div>

          <div className="space-y-2">
            <Label className="label-tiny">Business Deductible Rate ({symbolFor(currency)}/km)</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={deductibleRate}
              onChange={(e) => setDeductibleRate(e.target.value)}
              className="bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono-data"
              data-testid="settings-deductible-input"
            />
            <p className="text-[11px] text-[var(--text-muted)]">
              Applied to business mileage for the &ldquo;estimated deduction&rdquo; insight. IRS-style default is $0.70/km.
            </p>
          </div>

          {/* ---------------- Charging rates ---------------- */}
          <div className="pt-2 border-t border-[var(--border-subtle)]">
            <ChargingRatesSection
              initial={settings?.charging_rates}
              currencySymbol={symbolFor(currency)}
            />
          </div>

          {/* ---------------- Danger zone ---------------- */}
          <div
            className="mt-6 pt-5 border-t border-[var(--border-subtle)] space-y-3"
            data-testid="settings-danger-zone"
          >
            <div>
              <div className="text-[11px] uppercase tracking-[0.14em] text-[#E82127]">Danger zone</div>
              <p className="text-[11px] text-[var(--text-muted)] mt-1 leading-relaxed">
                Export your data anytime, or permanently delete your account. Both actions only affect your account — other users are not touched.
              </p>
            </div>

            <button
              type="button"
              onClick={() => setExportOptionsOpen(true)}
              disabled={exporting}
              className="w-full flex items-center justify-between bg-[var(--bg-elevated)] border border-[var(--border-subtle)] hover:border-white/30 px-4 py-3 text-left transition-colors disabled:opacity-60"
              style={{ borderRadius: 4 }}
              data-testid="settings-export-btn"
            >
              <div className="flex items-center gap-3 min-w-0">
                <Download className="w-4 h-4 text-[var(--text-primary)] flex-shrink-0" strokeWidth={1.75} />
                <div className="min-w-0">
                  <div className="text-sm text-[var(--text-primary)] tracking-tight">Export my data</div>
                  <div className="text-[11px] text-[var(--text-secondary)] truncate">
                    ZIP with trips.csv · energy.csv · vehicles.csv
                  </div>
                </div>
              </div>
              {exporting ? (
                <Loader2 className="w-3.5 h-3.5 text-[var(--text-primary)] animate-spin" strokeWidth={2} />
              ) : (
                <span className="text-[var(--text-faint)] text-xs">→</span>
              )}
            </button>

            <button
              type="button"
              onClick={() => setDeleteOpen(true)}
              className="w-full flex items-center justify-between bg-[var(--bg-elevated)] border border-[var(--border-danger-soft)] hover:border-[#E82127] px-4 py-3 text-left transition-colors group"
              style={{ borderRadius: 4 }}
              data-testid="settings-delete-account-btn"
            >
              <div className="flex items-center gap-3 min-w-0">
                <Trash2 className="w-4 h-4 text-[#E82127] flex-shrink-0" strokeWidth={1.75} />
                <div className="min-w-0">
                  <div className="text-sm text-[var(--text-primary)] tracking-tight">Delete my account</div>
                  <div className="text-[11px] text-[var(--text-secondary)] truncate">
                    Permanent · removes vehicles, trips, energy logs and profile
                  </div>
                </div>
              </div>
              <span className="text-[var(--text-faint)] text-xs group-hover:text-[#E82127] transition-colors">→</span>
            </button>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="bg-transparent border-[var(--border-subtle)] text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"
            data-testid="settings-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-[var(--bg-inverse)] text-[var(--text-inverse)] hover:bg-[var(--bg-inverse-hover)]"
            data-testid="settings-save-btn"
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
      <DeleteAccountDialog open={deleteOpen} onOpenChange={setDeleteOpen} />

      {/* Export-options confirmation — appears when user hits the
          "Export my data" button so they can consciously opt in to
          including archived-vehicle rows on a per-export basis. */}
      <Dialog open={exportOptionsOpen} onOpenChange={setExportOptionsOpen}>
        <DialogContent
          className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)] sm:max-w-[440px]"
          data-testid="export-options-dialog"
        >
          <DialogHeader>
            <div className="flex items-center gap-2">
              <Download className="w-4 h-4 text-[#E82127]" strokeWidth={1.75} />
              <DialogTitle className="text-lg tracking-tight">Export my data</DialogTitle>
            </div>
            <DialogDescription className="text-[13px] text-[var(--text-secondary)] leading-relaxed">
              We&apos;ll build a ZIP with <span className="font-mono-data text-[var(--text-primary)]">trips.csv</span> ·{" "}
              <span className="font-mono-data text-[var(--text-primary)]">energy.csv</span> ·{" "}
              <span className="font-mono-data text-[var(--text-primary)]">vehicles.csv</span> for your account.
            </DialogDescription>
          </DialogHeader>

          <label
            htmlFor="export-include-archived"
            className="flex items-start gap-2.5 bg-[var(--bg-elevated)] border border-[var(--border-subtle)] px-3 py-2.5 cursor-pointer select-none"
            style={{ borderRadius: 4 }}
            data-testid="export-options-include-row"
          >
            <Checkbox
              id="export-include-archived"
              checked={exportIncludeArchived}
              onCheckedChange={(v) => setExportIncludeArchived(v === true)}
              disabled={exporting}
              className="mt-0.5 border-[var(--border-hover)] data-[state=checked]:bg-[#E82127] data-[state=checked]:border-[#E82127]"
              data-testid="export-options-include-archived"
            />
            <div className="min-w-0 leading-snug">
              <div className="text-[13px] text-[var(--text-primary)]">
                Include archived vehicles
              </div>
              <div className="text-[11px] text-[var(--text-secondary)] mt-0.5">
                Adds trips and energy events for cars you&apos;ve archived. Default off — matches what you see on the dashboard.
              </div>
            </div>
          </label>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setExportOptionsOpen(false)}
              disabled={exporting}
              className="bg-transparent border-[var(--border-subtle)] text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"
              data-testid="export-options-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleExport}
              disabled={exporting}
              className="bg-[#E82127] hover:bg-[#C81C22] text-white"
              data-testid="export-options-confirm-btn"
            >
              {exporting ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" strokeWidth={2} />
                  Preparing…
                </>
              ) : (
                <>
                  <Download className="w-3.5 h-3.5 mr-2" strokeWidth={2} />
                  Download ZIP
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
